"use client";

import { useEffect } from "react";
import { useRouter } from "@/i18n/navigation";
import { useAuthStore } from "@/lib/stores/auth-store";
import { Sidebar } from "@/components/dashboard/sidebar";
import { TopBar } from "@/components/dashboard/top-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { startSignalR, stopSignalR } from "@/lib/signalr";
import { ReactivationBanner } from "./_components/ReactivationBanner";

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const { isAuthenticated, isLoading, loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      router.push("/login");
    }
  }, [isLoading, isAuthenticated, router]);

  // Start/stop SignalR connection with dashboard lifecycle
  useEffect(() => {
    if (isAuthenticated) {
      startSignalR();
    }
    return () => {
      stopSignalR();
    };
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="space-y-4 w-64">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen">
      <div className="aurora-dash" aria-hidden="true" />
      <Sidebar />
      <TopBar />
      <main
        className="p-4 lg:ms-64 lg:p-6"
        style={{ marginTop: "calc(4rem + var(--imp-banner-h, 0px))" }}
      >
        <div className="mx-auto w-full max-w-6xl">
          <ReactivationBanner />
          {children}
        </div>
      </main>
    </div>
  );
}
